import { IModuleChartConfig } from '@/interfaces/powerMarkets/charts/IModuleChartConfig.ts';
import {
    BoaTypes, IChartModule, IChartSeriesAdditionalDetails, ModuleSize, ModuleType, BoaIds,
} from '@/interfaces/powerMarkets/IModule.ts';
import { IDateRangeByNumberWithType } from '@/interfaces/general/IDateRangeByString.ts';
import { aggregateType } from '@/interfaces/powerMarkets/series/ISeriesOption.ts';
import {
    CalculationTypes, ICalculationItem, ICustomSeries, IPresetToEdit,
} from '@/interfaces/powerMarkets/dashboard/IDashboardChartCreatorOptions.ts';

import { IIdName } from '@/interfaces/general/IIdName.ts';
import { ISizePosition } from '@/interfaces/general/ISizePosition.ts';
import { ModulePresetType } from '@/interfaces/powerMarkets/IModulePreset.ts';
import { ICountry } from '@/interfaces/general/ICountry.ts';
import { ICategory, ISeries, ISeriesData } from '@/interfaces/powerMarkets/ISeriesData.ts';
import { getPresetAxisSideOptions } from '@/utils/presetAxisSides.ts';

function getChartDateObject (moduleChartConfig: IModuleChartConfig): IDateRangeByNumberWithType {
    const recentHour = moduleChartConfig.data.defaultTimeType === 'Hours';

    const daysBefore = moduleChartConfig.info.daysBefore === 0
        ? moduleChartConfig.data.daysBefore
        : (moduleChartConfig.info.daysBefore ?? moduleChartConfig.data.daysBefore ?? 0);
    const daysAfter = moduleChartConfig.info.daysAfter === 0
        ? moduleChartConfig.data.daysAfter
        : (moduleChartConfig.info.daysAfter ?? moduleChartConfig.data.daysAfter ?? 0);

    if (daysAfter === 0 && daysBefore === 0) {
        return { from: { type: 'date', value: 0 }, to: { type: 'date', value: 0 } };
    }
    return {
        recentHour,
        from: { type: 'dynamic', value: recentHour ? 1 : daysBefore },
        to: { type: 'dynamic', value: recentHour ? 1 : daysAfter },
        fromHours: recentHour ? daysBefore : 1,
        toHours: recentHour ? daysAfter : 1,
    };
}

function buildChartModulePayloadFromChartConfig (
    moduleChartConfig: IModuleChartConfig,
    countryName: string,
): IChartModule {
    const newModule: IChartModule = {
        chartSeries: [],
        date: getChartDateObject(moduleChartConfig),
        id: crypto.randomUUID(),
        name: `${moduleChartConfig.info.name} - ${countryName}`,
        nameSetByUser: true,
        legendHeight: ModuleSize.Medium,
        sizeAndPosition: getDefaultModulesSizing(ModuleType.Chart),
    };

    if (moduleChartConfig.data.axisToPercentage !== null && Array.isArray(moduleChartConfig.data.axisToPercentage)) {
        newModule.axisPositions = moduleChartConfig.data.axisToPercentage.map(el => ({ label: el.item1, value: el.item2, edited: true }));
    }
    if (moduleChartConfig.data.additionalDetails !== null) {
        newModule.additionalDetails = moduleChartConfig.data.additionalDetails;
    }
    const presetAxisSideOptions = getPresetAxisSideOptions(moduleChartConfig.data.axisSide);
    if (presetAxisSideOptions) {
        newModule.axisSidePositions = ['2'];
        newModule.axisLeftOrRight = presetAxisSideOptions;
    }

    moduleChartConfig.data.config.forEach(configForId => {
        const hasCalculator = Array.isArray(configForId.calculatorIdArray) && configForId.calculatorIdArray.length > 0;

        let seriesDetails: ReturnType<typeof deconstructSeriesDBId> | null = null;

        if (!hasCalculator) {
            seriesDetails = deconstructSeriesDBId(configForId.id);
        }

        newModule.chartSeries.push({
            dates: [],
            series: {
                seriesId: seriesDetails?.seriesId ?? null,
                optionsId: seriesDetails?.optionsId ?? null,
                country: seriesDetails?.country ?? null,
                calculatorIdArray: hasCalculator ? configForId.calculatorIdArray : null,
                isPlant: configForId.plantName !== null,
                multiplier: configForId.multiplier,
            },
        });

        const newSeries = newModule.chartSeries[newModule.chartSeries.length - 1];
        if (configForId.backendColourOverride) newSeries.series.colour = configForId.color;
        newSeries.series.chartType = configForId.type;
        if (configForId.opacity !== null && configForId.opacity !== undefined) newSeries.series.opacity = configForId.opacity;
        if (configForId.dashStyle !== null && configForId.dashStyle !== undefined) newSeries.series.dashStyle = configForId.dashStyle;
        if (configForId.nameOverride) newSeries.series.name = configForId.name;
        if (configForId.yAxisUnitOverride) newSeries.series.yAxisUnit = configForId.yAxisUnitEnum;
    });

    return newModule;
}

const moduleHeightSizing: Record<ModuleSize, number> = {
    ExtraSmall: 4,
    Small: 6,
    SmallSmallMedium: 7,
    SmallMedium: 8,
    Medium: 10,
    MediumLarge: 12,
    Large: 16,
    LargeMedium: 20,
    ExtraLarge: 20,
    LargeMediumLarge: 16,
};

const moduleWidthSizing: Record<ModuleSize, number> = {
    ExtraSmall: 2,
    Small: 3,
    SmallSmallMedium: 4,
    SmallMedium: 4,
    Medium: 6,
    MediumLarge: 8,
    LargeMedium: 8,
    Large: 12,
    ExtraLarge: 12,
    LargeMediumLarge: 9,
};

function getDefaultModulesSizing (
    moduleType: ModuleType | ModulePresetType | BoaTypes | null | BoaIds,
    customWidth?: number | string | null,
    customHeight?: number | string | null,
    size?: string | null,
): ISizePosition {
    const sizeAndPosition: ISizePosition = {
        h: moduleHeightSizing.Medium,
        w: moduleWidthSizing.Medium,
        x: 'auto',
        y: 'auto',
    };
    switch (moduleType) {
        case BoaIds.BalancingCostsGrid:
            sizeAndPosition.h = moduleHeightSizing.Large;
            sizeAndPosition.w = moduleWidthSizing.Large;
            break;
        case ModulePresetType.Metric:
            sizeAndPosition.h = 5;
            sizeAndPosition.w = moduleWidthSizing.SmallMedium;
            break;
        case ModulePresetType.DialMetric:
            sizeAndPosition.h = moduleHeightSizing.SmallMedium;
            sizeAndPosition.w = moduleWidthSizing.Small;
            break;
        case ModulePresetType.AncillaryMetric:
            sizeAndPosition.h = moduleHeightSizing.Medium;
            sizeAndPosition.w = moduleWidthSizing.Medium;
            break;
        case ModulePresetType.Treemap:
            sizeAndPosition.h = moduleHeightSizing.Large;
            break;
        case ModulePresetType.BoaLeaderboard:
            sizeAndPosition.w = moduleWidthSizing.Small;
            break;
        case BoaIds['BOA Stack']:
            sizeAndPosition.w = moduleWidthSizing.Small;
            break;
        case BoaIds['BOA Metrics']:
            sizeAndPosition.h = moduleHeightSizing.Small;
            sizeAndPosition.w = moduleWidthSizing.Small;
            break;
        case BoaIds['BOA Map']:
            sizeAndPosition.w = moduleWidthSizing.Small;
            break;
        case BoaIds['Constraints Map']:
            sizeAndPosition.w = moduleWidthSizing.Small;
            break;
        case ModulePresetType.NivEvolution:
            sizeAndPosition.h = moduleHeightSizing.Medium;
            sizeAndPosition.w = moduleWidthSizing.Large;
            break;
        case ModuleType.ReportPdfPage:
            sizeAndPosition.h = moduleHeightSizing.Large;
            sizeAndPosition.w = moduleWidthSizing.Medium;
            break;
        case ModuleType.IndexGroup:
        case ModuleType.Leaderboard:
        case ModuleType.StorageForecastChart:
            sizeAndPosition.h = moduleHeightSizing.Medium;
            sizeAndPosition.w = moduleWidthSizing.Large;
            break;
    }
    if (size && moduleHeightSizing[size as ModuleSize]) {
        sizeAndPosition.h = moduleHeightSizing[size as ModuleSize];
        sizeAndPosition.w = moduleWidthSizing[size as ModuleSize];
    }

    if (customWidth) {
        sizeAndPosition.w = typeof customWidth === 'number' ? customWidth : moduleWidthSizing[customWidth as ModuleSize];
    }
    if (customHeight) {
        sizeAndPosition.h = typeof customHeight === 'number' ? customHeight : moduleHeightSizing[customHeight as ModuleSize];
    }
    return sizeAndPosition;
}

function getSeriesJSONId (
    seriesId: string,
    optionsId: string | null,
    aggregateTypeValue: aggregateType | undefined,
    country: string,
): string {
    const ids = {
        seriesId,
        optionsId,
        aggregateType: aggregateTypeValue,
        country,
    };
    return JSON.stringify(ids);
}

function getSeriesDBId (
    seriesId: string | null,
    optionsId: string | null,
    country: string | null,
    calculatorIdArray?: Array<string> | null,
): string {
    if (calculatorIdArray) return calculatorIdArray.join('');

    const id = `${country || 'Gb'}&${seriesId}&${optionsId || 'none'}`;
    return id;
}

function deconstructSeriesDBId (id: string): { seriesId: string; optionsId: string | null; country: string } {
    const splitId = id.split('&');
    const hasCountry = splitId.length === 3;

    return {
        seriesId: splitId[hasCountry ? 1 : 0],
        optionsId: splitId[hasCountry ? 2 : 1] || null,
        country: hasCountry ? splitId[0] : 'Gb',
    };
}

function resolveCountryName (countriesById: Record<string, ICountry>, id: string | null): string | null {
    if (!id) return null;

    const matchingCountry = Object.values(countriesById).find(country => {
        const subCountries = country.subCountries ?? [];

        if (subCountries.length === 0 && country.id === id) {
            return true;
        }

        return subCountries.some(countryOption => countryOption.id === id);
    });

    if (!matchingCountry) return null;

    const subCountries = matchingCountry.subCountries ?? [];
    if (subCountries.length === 0) return matchingCountry.name;

    return subCountries.find(countryOption => countryOption.id === id)?.name ?? null;
}

function resolveSeriesOptionsName (series: ISeries, optionsId: string | null, country: string | null, isPlant: boolean): string | null {
    if (!optionsId || !country) return null;

    const optionNameParts: Array<string> = [];
    const optionsByCountry = series.optionsByCountry?.[country];
    const splitOptionsId = optionsId.split('*');

    if (isPlant) {
        optionNameParts.push(splitOptionsId[0]);
    }

    if (optionsByCountry && Object.keys(optionsByCountry.options).length > 0) {
        optionsByCountry.options.forEach((optionsGroup: Array<ICategory>, index: number) => {
            if (Object.keys(optionsGroup).length === 1) return;

            const optionId = splitOptionsId[isPlant ? index + 1 : index];
            const option = Object.values(optionsGroup).find(groupOption => groupOption.id === optionId);
            if (option?.name) optionNameParts.push(option.name);
        });
    } else if (Object.keys(series.options).length > 0) {
        const option = Object.values(series.options).find(groupOption => groupOption.id === optionsId);
        if (option?.name) optionNameParts.push(option.name);
    }

    return optionNameParts.join(' - ').trim() || null;
}

function getSeriesDisplayDetails (
    seriesId: string | null,
    optionsId: string | null,
    country: string | null,
    isPlant: boolean,
    dynamicSeries: ISeriesData | null,
    plantSeries: ISeriesData | null,
    countriesById: Record<string, ICountry>,
): { seriesName: string; optionsName: string | null; countryName: string | null } {
    const seriesData = isPlant ? plantSeries : dynamicSeries;
    const series = seriesId ? seriesData?.series[seriesId] : undefined;

    return {
        seriesName: series?.name ?? '',
        optionsName: series ? resolveSeriesOptionsName(series, optionsId, country, isPlant) : null,
        countryName: resolveCountryName(countriesById, country),
    };
}

function seriesIdIsPartialAggregateMatch (backEndId: string, country: string | null, seriesId: string): boolean {
    const id = `${country || 'Gb'}&${seriesId}`;
    if (backEndId.endsWith('&none')) return false;
    return backEndId.startsWith(id);
}

const seriesTypes: Record<string, IIdName> = {
    scatter: { name: 'Scatter', id: 'scatter' },
    line: { name: 'Line', id: 'line' },
    step: { name: 'Step', id: 'step' },
    area: { name: 'Area', id: 'area' },
    column: { name: 'Column', id: 'column' },
    default: { name: 'Default', id: 'default' },
};

const seriesDashType: Record<string, IIdName> = {
    defaultBackend: { id: 'defaultBackend', name: 'Default' },
    solid: { id: 'solid', name: 'Solid' },
    dot: { id: 'dot', name: 'Dot' },
    dash: { id: 'dash', name: 'Dash' },
    longDash: { id: 'longDash', name: 'Long Dash' },
    dashDot: { id: 'dashDot', name: 'Dash Dot' },
    shortDash: { id: 'shortDash', name: 'Short Dash' },
    shortDot: { id: 'shortDot', name: 'Short Dot' },
    shortDashDot: { id: 'shortDashDot', name: 'Short Dash Dot' },
    shortDashDotDot: { id: 'shortDashDotDot', name: 'Short Dash Dot Dot' },
    longDashDot: { id: 'longDashDot', name: 'Long Dash Dot' },
    longDashDotDot: { id: 'longDashDotDot', name: 'Long Dash Dot Dot' },
};

const missingCalculatorOptions: Record<string, IIdName> = {
    calc_hide: { id: 'calc_hide', name: 'Hide', info: 'If any series on this calculator has no data, then hide that point on the calculator.' },
    calc_zero: { id: 'calc_zero', name: 'Zero', info: 'If any series on this calculator has no data, then replace that series in the calculation with a 0.' },
    calc_one: { id: 'calc_one', name: 'One', info: 'If any series on this calculator has no data, then replace that series in the calculation with a 1.' },
};

const dataLabelOptions: Record<string, IIdName> = {
    none: { id: 'none', name: 'None' },
    start: { id: 'start', name: 'Start', info: 'Show a numeric label at the start of the current period on the chart.<br />This only works if the series has a value at that point.' },
    end: { id: 'end', name: 'End', info: 'Show a numeric label at the end of the current period on the chart.<br />This only works if the series has a value at that point.' },
    current: { id: 'current', name: 'Now', info: 'Show a numeric label at the current time on the chart.<br />This only works if the series is real-time and minutely.' },
    all: { id: 'all', name: 'All', info: 'Show a numeric label at all time points on the chart.<br />Note, this might get crowded.' },
    name: { id: 'name', name: 'Name', info: 'Show a text label for what series is on the chart.' },
};

const summaryGradingGranularities: Record<string, IIdName> = {
    none: { id: 'none', name: 'None' },
    Weekly: { id: 'Weekly', name: 'Week' },
    Monthly: { id: 'Monthly', name: 'Month' },
    Quarterly: { id: 'Quarterly', name: 'Quarter' },
    Yearly: { id: 'Yearly', name: 'Year' },
    All: { id: 'All', name: 'All' },
};
const summaryGradingTypes: Record<string, IIdName> = {
    all: { id: 'all', name: 'All', info: 'Calculate percentiles over all data points.' },
    min: { id: 'min', name: 'Min', info: 'Calculate percentiles over the minimum point of each day.' },
    max: { id: 'max', name: 'Max', info: 'Calculate percentiles over the maximum point of each day.' },
};

const axisStackOptions: Record<string, IIdName> = {
    overlap: { id: 'overlap', name: 'Same Chart', info: 'Series of different units appear on the same figure.' },
    stack: { id: 'stack', name: 'Different Charts ( % )', info: 'Series of different units appear in separate figures.' },
};

const axisSideOptions: Record<string, IIdName> = {
    same: { id: 'same', name: 'Left', info: 'Y axes appear on the same side (left)' },
    automatic: { id: 'automatic', name: 'Automatic', info: 'Y axes will appear on alternating opposite sides' },
    manual: { id: 'manual', name: 'Manual', info: 'Pick which side each Y-axis will appear on' },
};

const axisSidesPositions: Record<string, null | Array<string>> = {
    same: null,
    automatic: ['1'],
    manual: ['2'],
};

function getNewSeriesName (params: {
    seriesName: string;
    suboption?: IIdName;
    countryName: string | null;
    aggregateType?: aggregateType;
    isPlant: boolean;
}): string {
    if (params.aggregateType === aggregateType.plain && !params.isPlant) {
        return params.seriesName;
    }
    if (params.aggregateType === aggregateType.plain && params.isPlant) {
        return `${params.suboption?.name} - ${params.seriesName} - ${params.countryName}`;
    }
    if (params.aggregateType === aggregateType.historyOfForecast) {
        return `${params.seriesName} - ${params.countryName}`;
    }
    if (
        params.aggregateType === aggregateType.owner
        || params.aggregateType === aggregateType.zone
        || params.aggregateType === aggregateType.fuel
    ) {
        return `${params.seriesName} - ${params.suboption?.name} ${params.countryName}`;
    }

    if (params.aggregateType === aggregateType.calculator) {
        let returnValue = `${params.seriesName} -`;
        if (params.suboption) {
            returnValue += ` ${params.suboption.name} -`;
        }
        returnValue += ` ${params.countryName}`;
        return returnValue;
    }
    if (params.aggregateType === aggregateType.group) {
        return '';
    }

    return params.seriesName;
}

function getCombinedName (params: {
    seriesName: string;
    optionsName?: string | null;
    countryName: string | null;
}): string {
    const optionsString = params.optionsName === null || params.optionsName === undefined ? '' : ` - ${params.optionsName}`;
    const countryString = params.countryName === null || params.countryName === undefined ? '' : ` - ${params.countryName}`;

    return `${params.seriesName} ${optionsString} ${countryString}`;
}

function prepareCalculatorIdArrayDb (calculationArray: Array<ICalculationItem>): Array<string> {
    let stringArray: Array<string> = [];
    calculationArray.forEach(calculation => {
        if (calculation.type === CalculationTypes.num) {
            const numArray = calculation.value.split('');
            stringArray = stringArray.concat(numArray);
        }
        if (calculation.type === CalculationTypes.operator) {
            if (calculation.value === '÷') {
                stringArray.push('/');
            } else if (calculation.value === 'x') {
                stringArray.push('*');
            } else {
                stringArray.push(calculation.value);
            }
        }
        if (calculation.type === CalculationTypes.parenthesis) {
            stringArray.push(calculation.value);
        }
        if (calculation.type === CalculationTypes.series && calculation.seriesOption) {
            stringArray.push(calculation.seriesOption.seriesDbId);
        }
    });
    return stringArray;
}

function getCalculatorSeriesName (calculationArray: Array<ICalculationItem>): string {
    return calculationArray.map(calculation => {
        if (calculation.seriesOption) {
            return calculation.seriesOption.series.seriesName || '';
        }
        return calculation.value;
    }).join(' ');
}

function getAdditionalDetailsForSeriesCreation (): IChartSeriesAdditionalDetails {
    return {
        dashStyle: 'defaultBackend',
        dataLabelLocation: 'none',
        lineWidth: null,
        numberOfSeriesHof: 5,
        opacity: 1,
        prefix: null,
        stackGroup: null,
        suffix: null,
        summaryGradingGranularity: 'none',
        summaryGradingType: 'all',
    };
}

function getCalculatorSeriesDetails (calculationArray: Array<ICalculationItem>): string {
    const details: Array<string> = [];

    calculationArray.forEach(calculation => {
        if (calculation.type === CalculationTypes.series && calculation.seriesOption) {
            details.push(getNewSeriesName({
                seriesName: calculation.seriesOption.series.seriesName || '',
                suboption: calculation.seriesOption.series.optionsName && calculation.seriesOption.series.optionsId
                    ? { name: calculation.seriesOption.series.optionsName, id: calculation.seriesOption.series.optionsId }
                    : undefined,
                countryName: calculation.seriesOption.series.countryName,
                aggregateType: aggregateType.calculator,
                isPlant: false,
            }));
            details.push(' <br />');
        }
    });
    return details.join('');
}

function getCalculatorSeriesDetailsFromIdArray (
    calculatorIdArray: Array<string>,
    dynamicSeries: ISeriesData,
    countriesById: Record<string, ICountry>,
): string {
    const seriesDetails = calculatorIdArray
        .filter(calculatorItem => calculatorItem.includes('&'))
        .map(calculatorSeriesId => {
            const seriesIds = deconstructSeriesDBId(calculatorSeriesId);
            const displayDetails = getSeriesDisplayDetails(
                seriesIds.seriesId,
                seriesIds.optionsId,
                seriesIds.country,
                false,
                dynamicSeries,
                null,
                countriesById,
            );
            return getNewSeriesName({
                seriesName: displayDetails.seriesName,
                suboption: displayDetails.optionsName && seriesIds.optionsId
                    ? { name: displayDetails.optionsName, id: seriesIds.optionsId }
                    : undefined,
                countryName: displayDetails.countryName,
                aggregateType: aggregateType.calculator,
                isPlant: false,
            });
        });

    return seriesDetails.length > 0 ? `${seriesDetails.join(' <br />')} <br />` : '';
}

function getSeriesArrayFromPreset (
    chartPreset: IPresetToEdit,
    isForDarkMode: boolean,
    dynamicSeries: ISeriesData,
    plantSeries: ISeriesData,
    countriesById: Record<string, ICountry>,
): Array<ICustomSeries> {
    const selectedSeriesArray: Array<ICustomSeries> = [];
    chartPreset.config.data.config.forEach(seriesConfig => {
        const seriesDetails = deconstructSeriesDBId(seriesConfig.id);
        const seriesId = seriesDetails.seriesId;
        const seriesOption = seriesDetails.optionsId;
        const country = seriesDetails.country;

        const seriesUiId = getSeriesJSONId(
            seriesId,
            seriesOption,
            chartPreset.plantOwner.id,
            country,
        );
        const seriesDbId = getSeriesDBId(
            seriesId,
            seriesOption,
            country,
        );
        const displayDetails = getSeriesDisplayDetails(
            seriesId,
            seriesOption,
            country,
            chartPreset.isPlant,
            dynamicSeries,
            plantSeries,
            countriesById,
        );

        const newSeriesItem: ICustomSeries = {
            seriesDbId,
            seriesUiId,
            chartSeries: {
                dates: [],
                isForDarkMode,
                series: {
                    name: seriesConfig.name,
                    seriesId,
                    optionsId: seriesOption,
                    country,
                    isPlant: chartPreset.isPlant,
                    chartType: seriesConfig.type,
                    colour: seriesConfig.color,
                    aggregateType: chartPreset.plantOwner.id,
                    calculatorIdArray: null,
                    yAxisUnit: seriesConfig.yAxisUnitOverride ? seriesConfig.yAxisUnitEnum : null,
                },
            },
            uiDisplayDetails: {
                isPlant: chartPreset.isPlant && chartPreset.plantOwner.id === aggregateType.plain,
                countryName: displayDetails.countryName,
                optionsName: displayDetails.optionsName,
                ogSeriesName: displayDetails.seriesName,
                nameOverride: seriesConfig.nameOverride,
            },
        };
        selectedSeriesArray.push(newSeriesItem);
    });
    return selectedSeriesArray;
}

export {
    buildChartModulePayloadFromChartConfig,
    getChartDateObject,
    moduleHeightSizing,
    moduleWidthSizing,
    getSeriesJSONId,
    getSeriesDBId,
    getSeriesDisplayDetails,
    seriesIdIsPartialAggregateMatch,
    seriesTypes,
    seriesDashType,
    missingCalculatorOptions,
    dataLabelOptions,
    summaryGradingGranularities,
    summaryGradingTypes,
    axisSideOptions,
    axisStackOptions,
    axisSidesPositions,
    getNewSeriesName,
    getCombinedName,
    prepareCalculatorIdArrayDb,
    getAdditionalDetailsForSeriesCreation,
    getCalculatorSeriesName,
    getCalculatorSeriesDetails,
    getCalculatorSeriesDetailsFromIdArray,
    getSeriesArrayFromPreset,
    getDefaultModulesSizing,
    resolveCountryName,
};
